import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { SupplierDiscount, CreateSupplierDiscountRequest } from 'services/bidAnalysis/bidAnalysisServices';
import classes from '../ReferenceTables.module.scss';

interface SupplierDiscountModalProps {
  visible: boolean;
  onHide: () => void;
  onSave: () => void;
  editingDiscount: SupplierDiscount | null;
  discountForm: CreateSupplierDiscountRequest;
  onFormChange: (form: CreateSupplierDiscountRequest) => void;
  saving?: boolean;
}

export const SupplierDiscountModal = ({
  visible,
  onHide,
  onSave,
  editingDiscount,
  discountForm,
  onFormChange,
  saving = false,
}: SupplierDiscountModalProps) => {
  return (
    <Dialog
      header={editingDiscount ? 'Edit Supplier Discount' : 'Create Supplier Discount'}
      visible={visible}
      style={{ width: '500px' }}
      onHide={onHide}
      footer={
        <div>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={onHide}
            className="p-button-text"
            disabled={saving}
          />
          <Button
            label={editingDiscount ? 'Update' : 'Create'}
            icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={onSave}
            className={classes.primaryButton}
            loading={saving}
            disabled={saving}
          />
        </div>
      }
    >
      <div className={classes.formContent}>
        <div className={classes.formField}>
          <label htmlFor="supplier">Supplier *</label>
          <InputText
            id="supplier"
            value={discountForm.supplier}
            onChange={e => onFormChange({ ...discountForm, supplier: e.target.value })}
            placeholder="Enter supplier"
            className={classes.inputField}
          />
        </div>
        <div className={classes.formField}>
          <label htmlFor="supplier_name">Supplier Name *</label>
          <InputText
            id="supplier_name"
            value={discountForm.supplier_name}
            onChange={e => onFormChange({ ...discountForm, supplier_name: e.target.value })}
            placeholder="Enter supplier name"
            className={classes.inputField}
          />
        </div>
        <div className={classes.formField}>
          <label htmlFor="supplier_code">Supplier Code *</label>
          <InputText
            id="supplier_code"
            value={discountForm.supplier_code}
            onChange={e => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 5);
              onFormChange({ ...discountForm, supplier_code: v });
            }}
            onKeyPress={e => {
              if (!/^\d$/.test(e.key)) e.preventDefault();
            }}
            placeholder="5 digits"
            className={classes.inputField}
            maxLength={5}
          />
          {discountForm.supplier_code.length > 0 && !/^\d{5}$/.test(discountForm.supplier_code) && (
            <small className={classes.helpText} style={{ color: 'var(--red-500)' }}>
              Supplier code must be exactly 5 numeric digits
            </small>
          )}
        </div>
        <div className={classes.formField}>
          <label htmlFor="prompt_pay_discount">Prompt Pay Discount</label>
          <InputNumber
            id="prompt_pay_discount"
            value={discountForm.prompt_pay_discount ?? null}
            onValueChange={e => onFormChange({ ...discountForm, prompt_pay_discount: e.value ?? null })}
            placeholder="Enter prompt pay discount"
            className={classes.inputField}
            minFractionDigits={0}
            maxFractionDigits={4}
            mode="decimal"
          />
        </div>
        <div className={classes.formField}>
          <label htmlFor="admin_fee">Admin Fee</label>
          <InputNumber
            id="admin_fee"
            value={discountForm.admin_fee ?? null}
            onValueChange={e => onFormChange({ ...discountForm, admin_fee: e.value ?? null })}
            placeholder="Enter admin fee"
            className={classes.inputField}
            minFractionDigits={0}
            maxFractionDigits={4}
            mode="decimal"
          />
        </div>
      </div>
    </Dialog>
  );
};
